import { supabase, handleSupabaseError } from '../lib/supabase';

class CommunicationService {
  constructor() {
    this.supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
  }

  // Templates Management
  async createTemplate(templateData) {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session?.user?.id) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase?.from('communication_templates')?.insert({
          user_id: session?.user?.id,
          ...templateData
        })?.select()?.single();

      if (error) {
        throw new Error(`Failed to create template: ${error.message}`);
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error creating template:', error);
      return { success: false, error: handleSupabaseError(error) || error?.message };
    }
  }

  async getTemplates(type = null) {
    try {
      let query = supabase?.from('communication_templates')?.select('*')?.eq('is_active', true)?.order('created_at', { ascending: false });

      if (type) {
        query = query?.eq('type', type);
      }

      const { data, error } = await query;

      if (error) {
        throw new Error(`Failed to fetch templates: ${error.message}`);
      }

      return { success: true, data: data || [] };
    } catch (error) {
      console.error('Error fetching templates:', error);
      return { success: false, error: handleSupabaseError(error) || error?.message };
    }
  }

  async updateTemplate(templateId, templateData) {
    try {
      const { data, error } = await supabase?.from('communication_templates')?.update({
          ...templateData,
          updated_at: new Date()?.toISOString()
        })?.eq('id', templateId)?.select()?.single();

      if (error) {
        throw new Error(`Failed to update template: ${error.message}`);
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error updating template:', error);
      return { success: false, error: handleSupabaseError(error) || error?.message };
    }
  }

  // Contacts Management - FIXED to use correct columns
  async createContact(contactData) {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session?.user?.id) {
        throw new Error('User not authenticated');
      }

      // Process contact data to use correct column names
      const processedData = {
        user_id: session?.user?.id,
        name: contactData?.name || contactData?.full_name || 
              `${contactData?.first_name || ''} ${contactData?.last_name || ''}`?.trim(),
        email: contactData?.email,
        phone: contactData?.phone,
        company: contactData?.company,
        tags: contactData?.tags || [],
        is_active: contactData?.is_active !== undefined ? contactData?.is_active : true
      };

      const { data, error } = await supabase?.from('communication_contacts')?.insert(processedData)?.select()?.single();

      if (error) {
        throw new Error(`Failed to create contact: ${error.message}`);
      }

      // Transform response to include first_name and last_name for compatibility
      const transformedData = {
        ...data,
        first_name: data?.name?.split(' ')?.[0] || '',
        last_name: data?.name?.split(' ')?.slice(1)?.join(' ') || '',
        full_name: data?.name
      };

      return { success: true, data: transformedData };
    } catch (error) {
      console.error('Error creating contact:', error);
      return { success: false, error: handleSupabaseError(error) || error?.message };
    }
  }

  async getContacts() {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session?.user?.id) {
        throw new Error('User not authenticated');
      }

      // Select only existing columns
      const { data, error } = await supabase?.from('communication_contacts')
        ?.select('id, name, email, phone, company, tags, is_active, created_at, updated_at, last_contact_at')
        ?.eq('user_id', session?.user?.id)
        ?.eq('is_active', true)
        ?.order('created_at', { ascending: false });

      if (error) {
        throw new Error(`Failed to fetch contacts: ${error.message}`);
      }

      // Transform data to include first_name and last_name for compatibility
      const transformedData = (data || [])?.map(contact => ({
        ...contact,
        first_name: contact?.name?.split(' ')?.[0] || '',
        last_name: contact?.name?.split(' ')?.slice(1)?.join(' ') || '',
        full_name: contact?.name
      }));

      return { success: true, data: transformedData };
    } catch (error) {
      console.error('Error fetching contacts:', error);
      return { success: false, error: handleSupabaseError(error) || error?.message };
    }
  }

  // Campaign Management
  async createCampaign(campaignData) {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session?.user?.id) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase?.from('communication_campaigns')?.insert({
          user_id: session?.user?.id,
          ...campaignData
        })?.select()?.single();

      if (error) {
        throw new Error(`Failed to create campaign: ${error.message}`);
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error creating campaign:', error);
      return { success: false, error: handleSupabaseError(error) || error?.message };
    }
  }

  async getCampaigns() {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session?.user?.id) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase?.from('communication_campaigns')?.select(`
          *,
          template:communication_templates(name, type, content)
        `)?.eq('user_id', session?.user?.id)?.order('created_at', { ascending: false });

      if (error) {
        throw new Error(`Failed to fetch campaigns: ${error.message}`);
      }

      return { success: true, data: data || [] };
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      return { success: false, error: handleSupabaseError(error) || error?.message };
    }
  }

  // Message Management
  async createMessage(messageData) {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session?.user?.id) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase?.from('communication_messages')?.insert({
          user_id: session?.user?.id,
          ...messageData
        })?.select()?.single();

      if (error) {
        throw new Error(`Failed to create message: ${error.message}`);
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error creating message:', error);
      return { success: false, error: handleSupabaseError(error) || error?.message };
    }
  }

  async getMessages(filters = {}) {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session?.user?.id) {
        throw new Error('User not authenticated');
      }

      // CRITICAL FIX: Only select columns that actually exist in communication_contacts
      // The table has 'name' column, NOT 'first_name' or 'last_name'
      let query = supabase?.from('communication_messages')?.select(`
          *,
          contact:communication_contacts(id, name, email, phone, company),
          campaign:communication_campaigns(name),
          template:communication_templates(name)
        `)?.eq('user_id', session?.user?.id)?.order('created_at', { ascending: false });

      if (filters?.type) {
        query = query?.eq('type', filters?.type);
      }
      
      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }

      if (filters?.limit) {
        query = query?.limit(filters?.limit);
      }

      const { data, error } = await query;

      if (error) {
        throw new Error(`Failed to fetch messages: ${error.message}`);
      }

      // Transform contact data to split 'name' into 'first_name' and 'last_name' for UI compatibility
      const transformedData = (data || [])?.map(message => ({
        ...message,
        contact: message?.contact ? {
          ...message?.contact,
          first_name: message?.contact?.name?.split(' ')?.[0] || '',
          last_name: message?.contact?.name?.split(' ')?.slice(1)?.join(' ') || '',
          full_name: message?.contact?.name
        } : null
      }));

      return { success: true, data: transformedData };
    } catch (error) {
      console.error('Error fetching messages:', error);
      return { success: false, error: handleSupabaseError(error) || error?.message };
    }
  }

  async updateMessageStatus(messageId, status, metadata = {}) {
    try {
      const updateData = {
        status,
        metadata: { ...metadata },
        updated_at: new Date()?.toISOString()
      };

      // Set timestamp fields based on status
      const timestamp = new Date()?.toISOString();
      switch (status) {
        case 'sent':
          updateData.sent_at = timestamp;
          break;
        case 'delivered':
          updateData.delivered_at = timestamp;
          break;
        case 'opened':
          updateData.opened_at = timestamp;
          break;
        case 'clicked':
          updateData.clicked_at = timestamp;
          break;
      }

      const { data, error } = await supabase?.from('communication_messages')?.update(updateData)?.eq('id', messageId)?.select()?.single();

      if (error) {
        throw new Error(`Failed to update message status: ${error.message}`);
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error updating message status:', error);
      return { success: false, error: handleSupabaseError(error) || error?.message };
    }
  }

  // Communication Actions
  async sendEmail({ to, subject, html, fromName = 'LeadGen Pro', messageId = null }) {
    try {
      if (!this.supabaseUrl) {
        console.warn('Supabase URL not configured — cannot send email via edge function');
        return { success: false, error: 'Email service not configured' };
      }
      const { data: { session } } = await supabase?.auth?.getSession();
      const token = session?.access_token;

      const response = await fetch(`${this.supabaseUrl}/functions/v1/send-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token || import.meta.env?.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          to,
          subject,
          html,
          from_name: fromName
        })
      });

      const result = await response?.json();

      if (!result?.success) {
        throw new Error(result.error || 'Failed to send email');
      }

      // Update message status if messageId provided
      if (messageId) {
        await this.updateMessageStatus(messageId, 'sent', {
          external_id: result?.emailId,
          sent_via: 'resend'
        });
      }

      return { success: true, data: result };
    } catch (error) {
      console.error('Error sending email:', error);
      
      // Update message status to failed if messageId provided
      if (messageId) {
        await this.updateMessageStatus(messageId, 'failed', {
          error: error?.message
        });
      }
      
      return { success: false, error: handleSupabaseError(error) || error?.message };
    }
  }

  async sendSMS({ to, message, messageId = null }) {
    try {
      if (!this.supabaseUrl) {
        console.warn('Supabase URL not configured — cannot send SMS via edge function');
        return { success: false, error: 'SMS service not configured' };
      }
      const { data: { session } } = await supabase?.auth?.getSession();
      const token = session?.access_token;

      const response = await fetch(`${this.supabaseUrl}/functions/v1/send-sms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token || import.meta.env?.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          to,
          message
        })
      });

      const result = await response?.json();

      if (!result?.success) {
        throw new Error(result.error || 'Failed to send SMS');
      }

      // Update message status if messageId provided
      if (messageId) {
        await this.updateMessageStatus(messageId, 'sent', {
          external_id: result?.messageSid,
          sent_via: 'twilio',
          twilio_status: result?.status
        });
      }

      return { success: true, data: result };
    } catch (error) {
      console.error('Error sending SMS:', error);
      
      // Update message status to failed if messageId provided
      if (messageId) {
        await this.updateMessageStatus(messageId, 'failed', {
          error: error?.message
        });
      }
      
      return { success: false, error: handleSupabaseError(error) || error?.message };
    }
  }

  async makeCall({ to, message = null, record = false, messageId = null }) {
    try {
      if (!this.supabaseUrl) {
        console.warn('Supabase URL not configured — cannot make call via edge function');
        return { success: false, error: 'Calling service not configured' };
      }
      const { data: { session } } = await supabase?.auth?.getSession();
      const token = session?.access_token;

      const response = await fetch(`${this.supabaseUrl}/functions/v1/make-call`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token || import.meta.env?.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          to,
          message,
          record
        })
      });

      const result = await response?.json();

      if (!result?.success) {
        throw new Error(result.error || 'Failed to make call');
      }

      // Update message status if messageId provided
      if (messageId) {
        await this.updateMessageStatus(messageId, 'sent', {
          external_id: result?.callSid,
          sent_via: 'twilio',
          twilio_status: result?.status,
          call_to: result?.to,
          call_from: result?.from
        });
      }

      return { success: true, data: result };
    } catch (error) {
      console.error('Error making call:', error);
      
      // Update message status to failed if messageId provided
      if (messageId) {
        await this.updateMessageStatus(messageId, 'failed', {
          error: error?.message
        });
      }
      
      return { success: false, error: handleSupabaseError(error) || error?.message };
    }
  }

  // Usage Tracking Integration
  async trackUsage(resourceType, count = 1) {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session?.user?.id) {
        return { success: false, error: 'User not authenticated' };
      }

      // Get current period usage record
      const now = new Date();
      const currentPeriodStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const currentPeriodEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);

      const { data: existingRecord, error: fetchError } = await supabase?.from('usage_records')?.select('*')?.eq('user_id', session?.user?.id)?.eq('resource_type', resourceType)?.gte('period_start', currentPeriodStart?.toISOString())?.lte('period_end', currentPeriodEnd?.toISOString())?.single();

      if (fetchError && fetchError?.code !== 'PGRST116') {
        throw new Error(`Failed to fetch usage record: ${fetchError.message}`);
      }

      if (existingRecord) {
        // Update existing record
        const { data, error } = await supabase?.from('usage_records')?.update({
            usage_count: existingRecord?.usage_count + count,
            updated_at: now?.toISOString()
          })?.eq('id', existingRecord?.id)?.select()?.single();

        if (error) {
          throw new Error(`Failed to update usage: ${error.message}`);
        }

        return { success: true, data };
      } else {
        // Get user's subscription to determine limits
        const { data: subscription, error: subError } = await supabase?.from('subscriptions')?.select('plan_id, subscription_plans(features)')?.eq('user_id', session?.user?.id)?.eq('status', 'active')?.single();

        let usageLimit = 0;
        if (!subError && subscription?.subscription_plans?.features) {
          const features = subscription?.subscription_plans?.features;
          switch (resourceType) {
            case 'emails':
              usageLimit = features?.emails || features?.email || 0;
              break;
            case 'sms':
              usageLimit = features?.sms || features?.texts || 0;
              break;
            case 'calls':
              usageLimit = features?.calls || features?.calling || 0;
              break;
            default:
              usageLimit = features?.[resourceType] || 0;
          }
        }

        // Create new record
        const { data, error } = await supabase?.from('usage_records')?.insert({
            user_id: session?.user?.id,
            resource_type: resourceType,
            usage_count: count,
            usage_limit: usageLimit,
            period_start: currentPeriodStart?.toISOString(),
            period_end: currentPeriodEnd?.toISOString()
          })?.select()?.single();

        if (error) {
          throw new Error(`Failed to create usage record: ${error.message}`);
        }

        return { success: true, data };
      }
    } catch (error) {
      console.error('Error tracking usage:', error);
      return { success: false, error: handleSupabaseError(error) || error?.message };
    }
  }

  // Real-time subscriptions for communication updates
  subscribeToMessages(callback) {
    const channel = supabase?.channel('communication_messages')?.on(
        'postgres_changes',
        { 
          event: '*', 
          schema: 'public', 
          table: 'communication_messages' 
        },
        (payload) => {
          callback?.(payload);
        }
      )?.subscribe();

    return channel;
  }

  subscribeToCampaigns(callback) {
    const channel = supabase?.channel('communication_campaigns')?.on(
        'postgres_changes',
        { 
          event: '*', 
          schema: 'public', 
          table: 'communication_campaigns' 
        },
        (payload) => {
          callback?.(payload);
        }
      )?.subscribe();

    return channel;
  }

  unsubscribe(channel) {
    if (channel) {
      supabase?.removeChannel(channel);
    }
  }

  // Social Media Account Management
  async getSocialAccounts() {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session?.user?.id) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase?.from('social_media_accounts')?.select(`
          *,
          user_profiles!user_id(full_name, email)
        `)?.eq('user_id', session?.user?.id)?.order('created_at', { ascending: false });

      if (error) {
        throw new Error(`Failed to fetch social accounts: ${error.message}`);
      }

      return { success: true, data: data || [] };
    } catch (error) {
      console.error('Error fetching social accounts:', error);
      return { success: false, error: handleSupabaseError(error) || error?.message };
    }
  }

  async createSocialAccount(accountData) {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session?.user?.id) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase?.from('social_media_accounts')?.insert({
          user_id: session?.user?.id,
          ...accountData
        })?.select()?.single();

      if (error) {
        throw new Error(`Failed to create social account: ${error.message}`);
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error creating social account:', error);
      return { success: false, error: handleSupabaseError(error) || error?.message };
    }
  }

  async updateSocialAccount(accountId, accountData) {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session?.user?.id) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase?.from('social_media_accounts')?.update({
          ...accountData,
          updated_at: new Date()?.toISOString()
        })?.eq('id', accountId)?.eq('user_id', session?.user?.id)?.select()?.single();

      if (error) {
        throw new Error(`Failed to update social account: ${error.message}`);
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error updating social account:', error);
      return { success: false, error: handleSupabaseError(error) || error?.message };
    }
  }

  async disconnectSocialAccount(accountId) {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session?.user?.id) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase?.from('social_media_accounts')?.update({
          status: 'disconnected',
          access_token: null,
          refresh_token: null,
          updated_at: new Date()?.toISOString()
        })?.eq('id', accountId)?.eq('user_id', session?.user?.id)?.select()?.single();

      if (error) {
        throw new Error(`Failed to disconnect social account: ${error.message}`);
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error disconnecting social account:', error);
      return { success: false, error: handleSupabaseError(error) || error?.message };
    }
  }

  // Social Media Messaging
  async sendSocialMessage({ platform, message, recipientHandle, accountId = null, messageId = null }) {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      const token = session?.access_token;

      // Note: This is a placeholder for actual social media API integration
      // In a real implementation, you would integrate with platform-specific APIs:
      // - LinkedIn: LinkedIn API
      // - Facebook: Facebook Graph API
      // - Twitter: Twitter API v2
      // - Instagram: Instagram Basic Display API

      const mockResponse = {
        success: true,
        messageId: `${platform}_${Date.now()}`,
        status: 'sent',
        platform,
        timestamp: new Date()?.toISOString()
      };

      // Update message status if messageId provided
      if (messageId) {
        await this.updateMessageStatus(messageId, 'sent', {
          external_id: mockResponse?.messageId,
          sent_via: platform,
          platform_status: mockResponse?.status
        });
      }

      return { success: true, data: mockResponse };
    } catch (error) {
      console.error(`Error sending ${platform} message:`, error);
      
      // Update message status to failed if messageId provided
      if (messageId) {
        await this.updateMessageStatus(messageId, 'failed', {
          error: error?.message
        });
      }
      
      return { success: false, error: handleSupabaseError(error) || error?.message };
    }
  }

  // Enhanced usage tracking to include social media
  async trackSocialUsage(platform, count = 1) {
    try {
      const resourceTypes = {
        'linkedin': 'linkedin_messages',
        'facebook': 'facebook_messages', 
        'twitter': 'twitter_messages',
        'instagram': 'instagram_messages'
      };

      const resourceType = resourceTypes?.[platform] || 'social_messages';
      return await this.trackUsage(resourceType, count);
    } catch (error) {
      console.error('Error tracking social usage:', error);
      return { success: false, error: handleSupabaseError(error) || error?.message };
    }
  }

  // Enhanced message creation for social platforms
  async createSocialMessage(messageData) {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session?.user?.id) {
        throw new Error('User not authenticated');
      }

      // Find social account for the platform
      let socialAccountId = messageData?.social_account_id;
      if (!socialAccountId && messageData?.platform) {
        const { data: account, error: accountError } = await supabase?.from('social_media_accounts')?.select('id')?.eq('user_id', session?.user?.id)?.eq('platform', messageData?.platform)?.eq('status', 'connected')?.single();
        
        if (account) {
          socialAccountId = account?.id;
        }
      }

      const { data, error } = await supabase?.from('communication_messages')?.insert({
          user_id: session?.user?.id,
          type: 'social_message',
          social_account_id: socialAccountId,
          ...messageData
        })?.select()?.single();

      if (error) {
        throw new Error(`Failed to create social message: ${error.message}`);
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error creating social message:', error);
      return { success: false, error: handleSupabaseError(error) || error?.message };
    }
  }
}

export default new CommunicationService();